import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AifRatingBadge, RatingCircleBadge } from '@scope/scope-one-ui/core';
import { COLORS } from '@scope/scope-one-ui/theme';
import { RatingType } from '@scope/scope-one-ui/core/RatingCircleBadge/RatingCircleBadge.d';
import { ActionsButton, Datatable } from 'ui';
import { theme } from 'theme';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { useFeatures, usePortfolioModals, useSelectedTableRows, useTable } from 'shared/hooks';
import { SelectedEntities } from 'shared/types/portfolios';
import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { CreateNewPortfolioModal } from 'modules/common/Modals';
import { MUIDataTableMeta } from 'mui-datatables';
import { exportEntities } from 'services/export/exportService';

import { useDeleteFunds } from '../../GraphQL/portfolios/hooks';
import { useFetchPortfolioFunds } from '../../GraphQL/portfolios/hooks';

import { makeFundsTableData } from '../utils';
import {
  PortfolioItemsProps,
  PortfolioAction,
  FiltersData,
  FundsInitialFiltersData,
  PortfolioType,
  CollectionType,
} from '../types';
import { DatatableFundsFilters } from './components/DatatableFundsFilters';
import { DatatableChipFilters } from './components/DatatableChipFilters';
import { fetchInitialFiltersData } from './services/filtersService';
import { ratingTypes } from '../../../../shared/constants/ratingTypes';

const useStyles = makeStyles(() => ({
  root: {
    '& thead > tr': {
      whiteSpace: 'pre',
    },
  },
  buttonLink: {
    cursor: 'pointer',
  },
  fundRank: {
    width: '32px',
    height: '32px',
    padding: '5px',
    borderRadius: '6px',
    backgroundColor: '#e5eaef',
    textAlign: 'center',
    verticalAlign: 'middle',
    color: COLORS.primary.main,
  },
  fundRatingOuter: {
    width: '44px',
    height: '44px',
    padding: '4px',
    borderColor: '#e5eaef',
    borderRadius: '22px',
    borderWidth: '2px',
    borderStyle: 'solid',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  fundRatingInner: {
    width: '32px',
    height: '32px',
    padding: '5px',
    borderRadius: '16px',
    backgroundColor: COLORS.primary.main,
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    verticalAlign: 'middle',
  },
  fundPointsTotal: {
    color: '#c4c4c4',
  },
}));

export const PortfolioFunds: FC<PortfolioItemsProps> = ({
  portfolioId,
  total = 0,
  refetchPortfolio,
  isReferencePortfolio,
  portfolioData,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [updating, setUpdating] = useState(false);
  const {
    singleSelect,
    multiSelect,
    currentSelect,
    selectedAll,
    onSelectAll,
    onSelectedChange,
    selectSingle,
    clearSingle,
  } = useSelectedTableRows();
  const [shouldRefresh, setShouldRefresh] = useState({ value: false, reason: '' });
  const deleteFunds = useDeleteFunds();
  const { error, success } = useSnackbar();
  const [variant, setVariant] = useState<PortfolioAction>();
  const { createPortfolio, addToPortfolio } = usePortfolioModals();

  const features = useFeatures();
  const [filtersData, setFiltersData] = useState<FiltersData>({});
  const [filterFieldsInitialData, setFilterFieldsInitialData] = useState<FundsInitialFiltersData>();

  const { isDedicated } = portfolioData;

  /* ******************************************************************** */
  /*                            Event handlers                            */
  /* ******************************************************************** */

  const deleteItemsHandler = () => {
    setUpdating(true);
    deleteFunds({
      isins: selectedAll ? [] : Object.values(currentSelect.itemIds),
      portfolioId,
      selectedAll,
    })
      .then(({ data }) =>
        refreshList(true, refreshReasons.DELETED).finally(() =>
          success(data?.deleteInstruments?.message || t('Success')),
        ),
      )
      .catch(() => error(t('Unable to delete entities.')))
      .finally(() => setUpdating(false));
  };

  const copyItemsHandler = () => {
    setVariant(selectedAll ? 'copy_all' : 'copy');
    addToPortfolio.toggle();
  };

  const moveItemsHandler = () => {
    setVariant(selectedAll ? 'move_all' : 'move');
    addToPortfolio.toggle();
  };

  const exportToExcelHandler = () => {
    const items = Object.values(currentSelect.itemIds);

    // eslint-disable-next-line no-shadow
    const onError = (err: string) => error(err);

    exportEntities({
      items: selectedAll ? [] : items,
      onError,
      portfolioId,
      selectedAll,
      ratingType: ratingTypes.FUNDS,
    });
  };

  const exportFullDataToExcel = () => {
    const items = Object.values(currentSelect.itemIds);

    const onError = (err: string) => error(err);

    const onSuccess = () => success(t('TOOLBAR.EXPORT_STARTED'));

    exportEntities({
      items: selectedAll ? [] : items,
      onError,
      onSuccess,
      portfolioId,
      selectedAll,
      ratingType: ratingTypes.FUNDS,
      exportMode: 'extended',
    });
    clearSingle();
  };

  /* ******************************************************************** */
  /*                         Datatable filters                            */
  /* ******************************************************************** */

  useEffect(() => {
    if (features.popoverFiltersPortfolioManager) {
      fetchInitialFiltersData(portfolioId, CollectionType.FUNDS).then((response) =>
        setFilterFieldsInitialData(response),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiltersSubmit = (data: FiltersData) => {
    setFiltersData(data);
  };

  const handleDeleteFilter = (propertyName: keyof FiltersData) => {
    // eslint-disable-next-line camelcase
    if (filtersData) {
      // eslint-disable-next-line camelcase
      const { [propertyName]: _removedProperty, ...filtersEdited } = filtersData!;
      setFiltersData(filtersEdited);
    }
  };

  const handleClearAllFilters = () => setFiltersData({});

  const fundsFilters = filterFieldsInitialData && (
    <DatatableFundsFilters
      filterFieldsInitialData={filterFieldsInitialData}
      prevFiltersValues={filtersData}
      handleSubmit={handleFiltersSubmit}
    />
  );

  const filterChips = filtersData && (
    <DatatableChipFilters
      filtersData={filtersData}
      handleDelete={handleDeleteFilter}
      handleClearAll={handleClearAllFilters}
    />
  );

  /* ******************************************************************** */
  /*                         Custom body renders                          */
  /* ******************************************************************** */

  const nameRender = (name: string, { rowData: [isin] }: MUIDataTableMeta) => (
    <Button component="a" href={`${window.location.origin}/fund-analysis/fund-details/${isin}/`}>
      {name.toUpperCase()}
    </Button>
  );

  const actionRender = (_: unknown, { rowData: [id] }: MUIDataTableMeta) => (
    <ActionsButton onOpen={() => selectSingle(id)} actions={getActions(singleSelect)} />
  );

  const rankRenderer = (value: string) => (
    <div className={classes.fundRank}>
      <span>{value}</span>
    </div>
  );

  const ratingRenderer = (
    rating: RatingType,
    { rowData: [, , , , , , , , , isAif, isQualitative] }: MUIDataTableMeta,
  ) =>
    isAif ? (
      <AifRatingBadge size="tiny" rating={rating} />
    ) : (
      <RatingCircleBadge size="tiny" rank={rating} isQualitative={isQualitative} />
    );

  const pointsRenderer = (value: number) => (
    <span>
      {value || 0}
      <span className={classes.fundPointsTotal}>/100</span>
    </span>
  );

  const percentageRenderer = (value: number) => (value ? `${value.toFixed(2)}%` : 'N/A');

  /* ******************************************************************** */
  /*                       Table columns definition                       */
  /* ******************************************************************** */

  const initialColumns = useCallback(
    () => [
      {
        name: 'id',
        options: {
          display: false,
        },
      },
      {
        name: 'name',
        label: t('Name'),
        options: {
          customBodyRender: nameRender,
        },
      },
      {
        name: 'peergroup',
        label: t('Peergroup'),
      },
      {
        name: 'isin',
        label: t('ISIN'),
      },
      {
        name: 'rank',
        label: t('Rank'),
        options: {
          customBodyRender: rankRenderer,
        },
      },
      {
        name: 'rating',
        label: t('Rating'),
        options: {
          customBodyRender: ratingRenderer,
        },
      },
      {
        name: 'ratingPoints',
        label: t('Rating Points'),
        options: {
          customBodyRender: pointsRenderer,
        },
      },
      {
        name: 'return',
        label: t('Return 3Y (p.a.)'),
        options: {
          customBodyRender: percentageRenderer,
        },
      },
      {
        name: 'risk',
        label: t('Volatility 3Y (p.a.)'),
        options: {
          customBodyRender: percentageRenderer,
        },
      },

      {
        name: 'moreActions',
        label: ' ',
        options: {
          sort: false,
          customBodyRender: actionRender,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [singleSelect.count, currentSelect.itemIds],
  );

  /* ******************************************************************** */
  /*                             More actions                             */
  /* ******************************************************************** */

  const getActions = (items: SelectedEntities) => [
    {
      text: 'Copy to portfolio',
      handler: copyItemsHandler,
      hidden: items.count === 0,
      disabled: portfolioData.portfolioType === PortfolioType.SAMPLE_PORTFOLIO,
    },
    {
      text: 'Move to portfolio',
      handler: moveItemsHandler,
      hidden: items.count === 0 || isReferencePortfolio,
      disabled: isDedicated,
    },
    {
      text: 'Export View',
      handler: exportToExcelHandler,
      hidden: items.count === 0,
    },
    {
      text: 'Export Full Data',
      handler: exportFullDataToExcel,
      hidden: items.count === 0,
    },
    {
      text: 'Delete',
      handler: deleteItemsHandler,
      hidden: items.count === 0 || isReferencePortfolio,
      color: theme.palette.error.main,
      confirmation: {
        title: 'Deleting items',
        text: 'Do you really want to delete that items?',
      },
      disabled: isDedicated,
    },
  ];

  /* ******************************************************************** */
  /*                              Table data                              */
  /* ******************************************************************** */
  const { columns, pagination, onSortChange, onTableChange } = useTable({
    initialColumns,
  } as any);

  const { data, loading, refetch } = useFetchPortfolioFunds(
    portfolioId,
    pagination.page * pagination.limit,
    pagination.limit,
    pagination.sort && [
      {
        column: pagination.sort.sortColumn,
        direction: pagination.sort.direction,
      },
    ],
    filtersData,
  );

  const { fund: funds } = data || { fund: [] };

  const refreshList = async (refresh: boolean, reason: string) => {
    setUpdating(true);
    if (refetchPortfolio) {
      refetchPortfolio();
    }
    await refetch();
    setShouldRefresh({ value: refresh, reason });
    setUpdating(false);
  };

  const tableData = useMemo(() => makeFundsTableData(funds), [funds]);

  /* ******************************************************************** */
  /*                                Render                                */
  /* ******************************************************************** */
  return (
    <Box className={classes.root}>
      <Datatable
        loading={loading || updating}
        columns={columns}
        pagination={pagination}
        data={tableData}
        total={total}
        onSelectedChange={onSelectedChange}
        toolbarData={getActions(multiSelect)}
        onTableChange={onTableChange}
        onSortChange={onSortChange}
        selectedAll={selectedAll}
        onSelectAllClick={onSelectAll}
        shouldRefresh={shouldRefresh}
        removeShadow
        leftToolbar={<></>}
        showSelectionExtension={!isReferencePortfolio}
        popoverFilters={features.popoverFiltersPortfolioManager ? fundsFilters : <></>}
        filterChips={filterChips}
      />
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        onActionSuccess={refreshList}
        itemsData={currentSelect}
        variant={variant}
        itemType="FUND"
        sourcePortfolioId={portfolioId}
      />
      <CreateNewPortfolioModal
        itemsData={currentSelect}
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemType="FUND"
        actionVariant={variant}
        sourcePortfolioId={portfolioId}
      />
    </Box>
  );
};
